import React, { useState } from 'react'
import { requestPasswordReset } from '@lib/spektrix/customer'
import { validateEmail } from '@lib/validation'

const ResetPassword = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [emailError, setEmailError] = useState('')

  const handleEmailChange = (e) => {
    setEmail(e.target.value)
    setEmailError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setEmailError('')

    // Validate email
    if (!email.trim()) {
      setEmailError('Email address is required')
      return
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    setIsLoading(true)

    try {
      // Get domain from current window location
      const domain = window.location.hostname

      await requestPasswordReset(email, domain)

      // Always show success message (security best practice)
      setSubmitted(true)
    } catch (error) {
      console.error('Password reset request failed:', error)
      // Still show success message to avoid email enumeration
      setSubmitted(true)
    } finally {
      setIsLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="space-y-6">
        <div className="bg-green-50 dark:bg-green-900 dark:bg-opacity-20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400 dark:text-green-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                Check your email
              </h3>
              <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                <p>
                  If an account exists with the email address <strong>{email}</strong>,
                  you will receive a password reset link shortly.
                </p>
                <p className="mt-2">
                  Please check your inbox and spam folder.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <a
            href="/"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Return to home page
          </a>
        </div>
      </div>
    )
  }

  return (
    <div>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Enter your email address and we'll send you a link to reset your password.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Email Address
            <span className="text-red-500 dark:text-red-400 ml-1">*</span>
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={handleEmailChange}
            className={`w-full px-3 py-2 border ${
              emailError
                ? 'border-red-500 dark:border-red-400'
                : 'border-gray-300 dark:border-gray-600'
            } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
            placeholder="you@example.com"
            required
            disabled={isLoading}
            autoFocus
          />
          {emailError && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {emailError}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Sending...
            </span>
          ) : (
            'Send Reset Link'
          )}
        </button>

        <div className="text-center pt-4 space-y-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Remember your password?{' '}
            <a
              href="/my-account"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Sign in here
            </a>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Don't have an account?{' '}
            <a
              href="/register"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Create one here
            </a>
          </p>
        </div>
      </form>
    </div>
  )
}

export default ResetPassword
